import {
  faCirclePlus,
  faUsers,
  faCircleUserCircleXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Table, TextInput, Loader } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import styles from "@/styles/components/settings.module.scss";
import { gql } from "@/__generated__";
import { ProviderUserRole } from "@/__generated__/graphql";
import { Tip } from "design-system/src/components/Tip";
import { Toast } from "design-system/src/components/Toast";
import Navbar from "@/components/layout/Navbar";
import { Alert } from "design-system/src/components/Alert";
import { ProviderDefaultReferralMessage } from "../components/ProviderDefaultReferralMessage";
import { ClaimableModal } from "../components/shared/ClaimableModal";

export const GET_PROVIDER = gql(`
    query Provider {
        provider {
            providerId
            users {
                providerUserId
                providerId
                email
                firstName
                lastName
                jobTitle
                role
                active
                createdTimestamp
            }
        }
    }
`);

export const GET_PROVIDER_USER = gql(`
    query ProviderUser {
        providerUser {
            providerUserId
            role
        }
    }
`);

export const UPDATE_PROVIDER_USER_ACTIVE_STATUS = gql(`
    mutation UpdateProviderUserActiveStatus($providerUserId: ID!, $active: Boolean!) {
        updateProviderUserActiveStatus(providerUserId: $providerUserId, active: $active) {
            providerUserId
            active
        }
    }
`);

export const ADD_PROVIDER_USER = gql(`
    mutation AddNewUserToProvider($email: String!) {
      addNewUserToProvider(email: $email) {
        providerUserId
      }
    }
`);

type AddUserForm = {
  email: string;
};

type User = {
  providerUserId: string;
  providerId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  jobTitle?: string | null;
  role: ProviderUserRole;
  active: boolean;
  createdTimestamp: string;
};

function Settings() {
  const [isUnableToAddUser, setIsUnableToAddUser] = useState(false);
  const [deactivateModalUser, setDeactivateModalUser] = useState<User | null>(
    null,
  );

  const [addProviderUser, { loading, error, data: mutationData }] =
    useMutation(ADD_PROVIDER_USER);

  const [updateProviderUserActiveStatus, { loading: updateLoading }] =
    useMutation(UPDATE_PROVIDER_USER_ACTIVE_STATUS, {
      refetchQueries: [{ query: GET_PROVIDER }],
    });

  const { key, onSubmit, getInputProps, reset } = useForm<AddUserForm>({
    initialValues: {
      email: "",
    },
    validate: {
      email: (value) => {
        if (!value || value.trim() === "") {
          return "Email address is required";
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value)) {
          return "Valid email address required";
        }
        return null;
      },
    },
  });

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
    refetch,
  } = useQuery(GET_PROVIDER);

  const { data: currentUserData } = useQuery(GET_PROVIDER_USER);

  const users = queryData?.provider?.users || [];
  const currentUser = currentUserData?.providerUser;
  const isAdmin = currentUser?.role === ProviderUserRole.Admin;

  const addUser = async (formData: AddUserForm) => {
    setIsUnableToAddUser(false);

    const { email } = formData;

    try {
      await addProviderUser({
        variables: {
          email,
        },
      });
      reset();
      refetch();
    } catch {
      setIsUnableToAddUser(true);
    }
  };

  const getRoleDisplay = (role: ProviderUserRole) => {
    return role === ProviderUserRole.Admin ? "Admin" : "User";
  };

  const getUserFullName = (user: User) => {
    const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return fullName || "N/A";
  };

  const handleDeactivateUser = async (user: User) => {
    try {
      await updateProviderUserActiveStatus({
        variables: {
          providerUserId: user.providerUserId,
          active: !user.active,
        },
      });
      setDeactivateModalUser(null);
    } catch (error) {
      console.error("Error updating user status:", error);
      alert(
        `Failed to ${user.active ? "deactivate" : "reactivate"} user. Please check if the database migration has been applied.`,
      );
      setDeactivateModalUser(null);
    }
  };

  const openDeactivateModal = (user: User) => {
    setDeactivateModalUser(user);
  };

  const closeDeactivateModal = () => {
    setDeactivateModalUser(null);
  };

  // Filter users based on role - only show active users to non-admin users
  const filteredUsers = isAdmin
    ? users
    : users.filter((user: User) => user.active);

  return (
    <Navbar titleText="Settings">
      <div className={styles.pageRoot}>
        <div className={styles.usersSection}>
          <span className={styles.usersHeader}>
            <FontAwesomeIcon icon={faUsers} />
            {"Users"}
          </span>
          <Toast
            variant="success"
            isVisible={!!mutationData && !isUnableToAddUser}
          >
            Successfully added user
          </Toast>
          <Toast variant="error" isVisible={!!queryError}>
            {queryError?.message}
          </Toast>
          <Toast variant="error" isVisible={!!error}>
            {error?.message}
          </Toast>
          <span className={styles.usersText}>
            {"Add coworkers who need to send referrals or track appeal status."}
          </span>
          <div className={styles.addUserForm}>
            <form
              id="addUser"
              className={styles.form}
              onSubmit={onSubmit(addUser)}
            >
              <TextInput
                label="Email address"
                key={key("email")}
                {...getInputProps("email")}
                classNames={{
                  input: styles.emailInput,
                  label: styles.emailLabel,
                }}
                onChange={(email) => {
                  getInputProps("email").onChange(email);
                }}
                data-testid="email_input"
              />
              {isUnableToAddUser && (
                <Alert variant="error">
                  <Alert.Title>Something went wrong</Alert.Title>
                  <Alert.Caption>
                    Unable to send an invite to this email. Please contact
                    support to resolve this issue.
                  </Alert.Caption>
                  <Alert.Link href="mailto:support@getclaimable.com">
                    Contact Support
                  </Alert.Link>
                </Alert>
              )}
              <button
                className={`
                    ${styles.addUserButton}
                    ${loading ? "loading_button" : ""}
                    `}
                type="submit"
                disabled={loading}
                data-testid="add_user_button"
              >
                {loading ? (
                  <Loader color="black" />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCirclePlus} />
                    {"Add user"}
                  </>
                )}
              </button>
            </form>
          </div>
          {queryLoading && <Loader color="black" />}
          {!queryLoading && !queryError && (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{"Email"}</Table.Th>
                  <Table.Th>{"Name"}</Table.Th>
                  <Table.Th>{"Job Title"}</Table.Th>
                  <Table.Th>{"Role"}</Table.Th>
                  {isAdmin && <Table.Th></Table.Th>}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredUsers.map((user: User) => {
                  const isDeactivated = !user.active;
                  const canDeactivate =
                    isAdmin &&
                    user.providerUserId !== currentUser?.providerUserId;
                  return (
                    <Table.Tr key={user.providerUserId}>
                      <Table.Td
                        className={isDeactivated ? styles.deactivatedUser : ""}
                      >
                        {user.email}
                      </Table.Td>
                      <Table.Td
                        className={isDeactivated ? styles.deactivatedUser : ""}
                      >
                        {getUserFullName(user)}
                      </Table.Td>
                      <Table.Td
                        className={isDeactivated ? styles.deactivatedUser : ""}
                      >
                        {user.jobTitle || "N/A"}
                      </Table.Td>
                      <Table.Td
                        className={isDeactivated ? styles.deactivatedUser : ""}
                      >
                        {getRoleDisplay(user.role)}
                      </Table.Td>
                      {isAdmin && (
                        <Table.Td>
                          {canDeactivate && (
                            <button
                              type="button"
                              className={styles.deactivateButton}
                              onClick={() => openDeactivateModal(user)}
                              aria-label={
                                isDeactivated
                                  ? "Reactivate user"
                                  : "Deactivate user"
                              }
                              disabled={updateLoading}
                            >
                              <FontAwesomeIcon icon={faCircleUserCircleXmark} />
                            </button>
                          )}
                        </Table.Td>
                      )}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}

          <Tip>
            <Tip.Heading>{"Need to remove a user?"}</Tip.Heading>
            <Tip.Caption>
              {"Email support and we'll be happy to take care of that for you."}
            </Tip.Caption>
            <Tip.Link href="mailto:providers@getclaimable.com">
              {"Providers@getclaimable.com"}
            </Tip.Link>
          </Tip>
        </div>
        <ProviderDefaultReferralMessage />
      </div>

      <ClaimableModal
        user={deactivateModalUser}
        isOpen={!!deactivateModalUser}
        onClose={closeDeactivateModal}
        onConfirm={handleDeactivateUser}
        isLoading={updateLoading}
      />
    </Navbar>
  );
}

export default Settings;
